import * as THREE from 'three';
import { AIRPLANE } from './constants';

/**
 * Creates and returns an airplane model with all its components
 */
const createAirplane = () => {
  // Create an airplane group to hold the model and the lidar equipment
  const airplaneGroup = new THREE.Group();
  
  // Create a more detailed airplane using built-in geometries
  const planeBody = new THREE.Group();
  
  // Main fuselage (body)
  const fuselageGeometry = new THREE.CylinderGeometry(
    AIRPLANE.FUSELAGE.DIAMETER_TOP, 
    AIRPLANE.FUSELAGE.DIAMETER_BOTTOM, 
    AIRPLANE.FUSELAGE.LENGTH, 
    8
  );
  const fuselageMaterial = new THREE.MeshPhongMaterial({ color: 0xf0f0f0 });
  const fuselage = new THREE.Mesh(fuselageGeometry, fuselageMaterial);
  fuselage.rotation.z = Math.PI / 2; // Rotate to horizontal
  planeBody.add(fuselage);
  
  // Nose cone
  const noseGeometry = new THREE.ConeGeometry(0.3, 0.6, 8);
  const nose = new THREE.Mesh(noseGeometry, fuselageMaterial);
  nose.rotation.z = -Math.PI / 2; // Point forward
  nose.position.set(1.3, 0, 0);
  planeBody.add(nose);
  
  // Cockpit/windshield
  const cockpitGeometry = new THREE.SphereGeometry(0.32, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  const cockpitMaterial = new THREE.MeshPhongMaterial({ 
    color: 0x8cdbff,
    transparent: true,
    opacity: 0.7,
    shininess: 100 
  });
  const cockpit = new THREE.Mesh(cockpitGeometry, cockpitMaterial);
  cockpit.rotation.x = Math.PI;
  cockpit.position.set(0.4, 0.3, 0);
  planeBody.add(cockpit);
  
  // Create more detailed Cessna-like wings using multiple geometries
  const wingMaterial = new THREE.MeshPhongMaterial({ color: 0xd1d1d1 });
  
  // Main wing group
  const wingsGroup = new THREE.Group();
  
  // Main wing center section (rectangular)
  const wingCenterGeometry = new THREE.BoxGeometry(0.6, 0.08, 2.8);
  const wingCenter = new THREE.Mesh(wingCenterGeometry, wingMaterial);
  wingsGroup.add(wingCenter);
  
  // Wing struts (Cessna high-wing design)
  const strutMaterial = new THREE.MeshPhongMaterial({ color: 0x999999 });
  
  // Left wing strut
  const leftStrutGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.5, 8);
  const leftStrut = new THREE.Mesh(leftStrutGeometry, strutMaterial);
  leftStrut.position.set(0, -0.2, -0.8);
  leftStrut.rotation.z = -0.2;
  wingsGroup.add(leftStrut);
  
  // Right wing strut
  const rightStrutGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.5, 8);
  const rightStrut = new THREE.Mesh(rightStrutGeometry, strutMaterial);
  rightStrut.position.set(0, -0.2, 0.8);
  rightStrut.rotation.z = -0.2;
  wingsGroup.add(rightStrut);
  
  // Wing tip sections (tapered for realism)
  // Left wingtip
  const leftWingTipGeometry = new THREE.BoxGeometry(0.5, 0.08, 0.7);
  const leftWingTip = new THREE.Mesh(leftWingTipGeometry, wingMaterial);
  leftWingTip.position.set(0, 0, -1.7);
  // Add slight dihedral angle (upward angle of wings)
  leftWingTip.rotation.x = -0.05;
  wingsGroup.add(leftWingTip);
  
  // Right wingtip
  const rightWingTipGeometry = new THREE.BoxGeometry(0.5, 0.08, 0.7);
  const rightWingTip = new THREE.Mesh(rightWingTipGeometry, wingMaterial);
  rightWingTip.position.set(0, 0, 1.7);
  rightWingTip.rotation.x = 0.05;
  wingsGroup.add(rightWingTip);
  
  // Position the wing group (high-wing configuration typical of Cessna)
  wingsGroup.position.set(0, 0.2, 0);
  planeBody.add(wingsGroup);
  
  // Tail section group
  const tailGroup = new THREE.Group();
  
  // Horizontal stabilizer (more realistic shape)
  const tailGeometry = new THREE.BoxGeometry(0.45, 0.06, 1.2);
  const tail = new THREE.Mesh(tailGeometry, wingMaterial);
  tail.position.set(-1.1, 0.15, 0);
  tailGroup.add(tail);
  
  // Vertical stabilizer (more accurate shape)
  const vStabGeometry = new THREE.BoxGeometry(0.7, 0.5, 0.06);
  const vStab = new THREE.Mesh(vStabGeometry, wingMaterial);
  vStab.position.set(-1.1, 0.4, 0);
  tailGroup.add(vStab);
  
  // Rudder (control surface on vertical stabilizer)
  const rudderGeometry = new THREE.BoxGeometry(0.3, 0.4, 0.04);
  const rudder = new THREE.Mesh(rudderGeometry, wingMaterial);
  rudder.position.set(-1.3, 0.4, 0);
  tailGroup.add(rudder);
  
  // Elevator (control surfaces on horizontal stabilizer)
  const elevatorGeometry = new THREE.BoxGeometry(0.15, 0.05, 1.15);
  const elevator = new THREE.Mesh(elevatorGeometry, wingMaterial);
  elevator.position.set(-1.3, 0.15, 0);
  tailGroup.add(elevator);
  
  planeBody.add(tailGroup);
  
  // Propeller hub
  const hubGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.12, 8);
  const hubMaterial = new THREE.MeshPhongMaterial({ color: 0x333333 });
  const hub = new THREE.Mesh(hubGeometry, hubMaterial);
  hub.rotation.x = Math.PI / 2;
  hub.position.set(1.6, 0, 0);
  planeBody.add(hub);
  
  // Propeller blades
  const propellerGroup = new THREE.Group();
  const propellerGeometry = new THREE.BoxGeometry(0.1, 0.02, 1.2);
  const propellerMaterial = new THREE.MeshPhongMaterial({ color: 0x333333 });
  
  // First blade
  const propeller1 = new THREE.Mesh(propellerGeometry, propellerMaterial);
  propellerGroup.add(propeller1);
  
  // Second blade (perpendicular)
  const propeller2 = new THREE.Mesh(propellerGeometry, propellerMaterial);
  propeller2.rotation.z = Math.PI / 2;
  propellerGroup.add(propeller2);
  
  propellerGroup.position.set(1.7, 0, 0);
  planeBody.add(propellerGroup);
  
  // Landing gear
  const wheelGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.08, 16);
  wheelGeometry.rotateX(Math.PI / 2);
  const wheelMaterial = new THREE.MeshPhongMaterial({ color: 0x222222 });
  
  // Front wheel
  const frontWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  frontWheel.position.set(1, -0.4, 0);
  planeBody.add(frontWheel);
  
  // Main wheels (under wings)
  const leftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  leftWheel.position.set(-0.2, -0.4, -0.8);
  planeBody.add(leftWheel);
  
  const rightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  rightWheel.position.set(-0.2, -0.4, 0.8);
  planeBody.add(rightWheel);
  
  // Struts for landing gear
  const gearStrutGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.4, 8);
  const gearStrutMaterial = new THREE.MeshPhongMaterial({ color: 0x888888 });
  
  // Front strut
  const frontStrut = new THREE.Mesh(gearStrutGeometry, gearStrutMaterial);
  frontStrut.position.set(1, -0.2, 0);
  planeBody.add(frontStrut);
  
  // Main struts
  const leftGearStrut = new THREE.Mesh(gearStrutGeometry, gearStrutMaterial);
  leftGearStrut.position.set(-0.2, -0.2, -0.8);
  planeBody.add(leftGearStrut);
  
  const rightGearStrut = new THREE.Mesh(gearStrutGeometry, gearStrutMaterial);
  rightGearStrut.position.set(-0.2, -0.2, 0.8);
  planeBody.add(rightGearStrut);
  
  // Details - windows
  const windowGeometry = new THREE.PlaneGeometry(0.15, 0.15);
  const windowMaterial = new THREE.MeshPhongMaterial({ 
    color: 0x88ccee, 
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.7
  });
  
  // Add windows along fuselage
  for (let i = 0; i < 3; i++) {
    const leftWindow = new THREE.Mesh(windowGeometry, windowMaterial);
    leftWindow.position.set(-0.3 - (i * 0.3), 0.2, -0.3);
    leftWindow.rotation.y = Math.PI / 2;
    planeBody.add(leftWindow);
    
    const rightWindow = new THREE.Mesh(windowGeometry, windowMaterial);
    rightWindow.position.set(-0.3 - (i * 0.3), 0.2, 0.3);
    rightWindow.rotation.y = Math.PI / 2;
    planeBody.add(rightWindow);
  }
  
  // Scale and rotate the airplane to match scene dimensions
  planeBody.scale.set(AIRPLANE.SCALE, AIRPLANE.SCALE, AIRPLANE.SCALE);
  airplaneGroup.add(planeBody);
  
  // Position the airplane just above the earth's surface
  airplaneGroup.position.set(0, AIRPLANE.POSITION.HEIGHT, 0);
  airplaneGroup.rotation.x = AIRPLANE.TILT;  // Slight tilt for flying attitude
  
  return {
    airplaneGroup,
    planeBody,
    propellerGroup,
    geometries: {
      fuselageGeometry,
      noseGeometry,
      cockpitGeometry,
      wingCenterGeometry,
      leftWingTipGeometry,
      rightWingTipGeometry,
      leftStrutGeometry,
      rightStrutGeometry,
      tailGeometry,
      vStabGeometry,
      rudderGeometry,
      elevatorGeometry,
      hubGeometry,
      propellerGeometry,
      wheelGeometry,
      gearStrutGeometry,
      windowGeometry
    },
    materials: {
      fuselageMaterial,
      cockpitMaterial,
      wingMaterial,
      strutMaterial,
      hubMaterial,
      propellerMaterial,
      wheelMaterial,
      gearStrutMaterial,
      windowMaterial
    }
  };
};

/**
 * Updates airplane position and orientation in orbital path
 */
const updateAirplanePosition = (airplaneGroup, planeBody) => {
  const time = Date.now() * 0.001;
  const orbit = AIRPLANE.ORBIT;
  
  // Calculate longitude progression (always moving forward)
  const longitude = (time * orbit.LONGITUDE_SPEED) % 360;
  
  // Get the base latitude for the circular orbit
  const baseLat = orbit.LATITUDE_BASE;
  
  // For circular orbit - modified to ensure East-West orbit at 30° North
  if (orbit.PATTERN === 'CIRCULAR') {
    const baseLatRad = baseLat * (Math.PI / 180);
    const lonRad = longitude * (Math.PI / 180);
    
    const radius = orbit.RADIUS;
    
    // Directly calculate position at the desired latitude
    // For a perfectly East-West orbit at latitude 30°
    const phi = (90 - baseLat) * (Math.PI / 180); // Convert to spherical coordinates
    const theta = lonRad;
    
    // Calculate position using spherical coordinates
    // This ensures we stay at exactly 30° North with no tilt
    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi); // This will be fixed for a given latitude
    const z = radius * Math.sin(phi) * Math.sin(theta);
    
    // Set position directly without any quaternion rotations
    airplaneGroup.position.set(x, y, z);
  } else {
    // Fallback to a regular circular orbit at fixed latitude
    const phi = (90 - baseLat) * (Math.PI / 180);
    const theta = longitude * (Math.PI / 180);
    
    // Position the airplane - radius is just above Earth's surface
    const radius = orbit.RADIUS;
    airplaneGroup.position.x = radius * Math.sin(phi) * Math.cos(theta);
    airplaneGroup.position.y = radius * Math.cos(phi);
    airplaneGroup.position.z = radius * Math.sin(phi) * Math.sin(theta);
  }
  
  // Calculate next position for forward orientation (look ahead)
  const lonAhead = (longitude + orbit.DIRECTION_AHEAD_VALUE) % 360;
  
  // Create a vector for the look-ahead position
  let lookAheadPosition;
  
  if (orbit.PATTERN === 'CIRCULAR') {
    // Calculate look-ahead position using the same coordinate calculation
    const lonAheadRad = lonAhead * (Math.PI / 180);
    const radius = orbit.RADIUS;
    
    // Use the same spherical coordinate calculation for look-ahead point
    const phi = (90 - baseLat) * (Math.PI / 180);
    const theta = lonAheadRad;
    
    // Calculate position using spherical coordinates
    const xAhead = radius * Math.sin(phi) * Math.cos(theta);
    const yAhead = radius * Math.cos(phi); // This will be fixed for a given latitude
    const zAhead = radius * Math.sin(phi) * Math.sin(theta);
    
    // Create a temporary vector for the look-ahead position
    lookAheadPosition = new THREE.Vector3(xAhead, yAhead, zAhead);
  } else {
    // Fallback to regular look-ahead calculation
    const phiAhead = (90 - baseLat) * (Math.PI / 180);
    const thetaAhead = lonAhead * (Math.PI / 180);
    
    const radius = orbit.RADIUS;
    const xAhead = radius * Math.sin(phiAhead) * Math.cos(thetaAhead);
    const yAhead = radius * Math.cos(phiAhead);
    const zAhead = radius * Math.sin(phiAhead) * Math.sin(thetaAhead);
    
    lookAheadPosition = new THREE.Vector3(xAhead, yAhead, zAhead);
  }
  
  // Create vectors for orientation
  const planePosition = new THREE.Vector3().copy(airplaneGroup.position);
  
  // Direction of travel - always forward along the orbit
  const forwardVector = new THREE.Vector3()
    .subVectors(lookAheadPosition, planePosition)
    .normalize();
  
  // Direction to center of Earth (down)
  const downVector = new THREE.Vector3()
    .copy(planePosition)
    .negate()  // Point toward earth center
    .normalize();
  
  // Right vector (perpendicular to both forward and down)
  const rightVector = new THREE.Vector3()
    .crossVectors(forwardVector, downVector)
    .normalize();
  
  // Recalculate up vector to ensure orthogonality
  const upVector = new THREE.Vector3()
    .crossVectors(rightVector, forwardVector)
    .normalize();
  
  // Create rotation matrix from orthogonal vectors
  const rotationMatrix = new THREE.Matrix4();
  
  // Always face forward in direction of travel
  rotationMatrix.makeBasis(rightVector, upVector, forwardVector.negate());
  
  // Apply rotation
  airplaneGroup.quaternion.setFromRotationMatrix(rotationMatrix);
  
  // Fine-tune orientation to match plane's coordinate system
  airplaneGroup.rotateY(-Math.PI / 2);  // Orient nose forward
  airplaneGroup.rotateZ(Math.PI);       // Flip the plane right-side up
};

/**
 * Cleanup function for airplane objects
 */
const cleanupAirplane = (scene, airplane) => {
  const { airplaneGroup, geometries, materials } = airplane;
  
  scene.remove(airplaneGroup);
  
  // Dispose of all geometries
  Object.values(geometries).forEach(geometry => {
    geometry.dispose();
  });
  
  // Dispose of all materials
  Object.values(materials).forEach(material => {
    material.dispose();
  });
};

export { createAirplane, updateAirplanePosition, cleanupAirplane };